// app/ask-cimigo/components/PlansSection.tsx
"use client";

import { APP_ROUTES } from "@/constants/routes";
import { usePrice } from "@/helper/usePrice";
import { cn } from "@/lib/utils";
import { ConfigData } from "@/models/config";
import { TYPE_NEED_HELP } from "@/models/general";
import { paramsSubcription } from "@/models/Params";
import { User } from "@/models/user";
import SubscriptionServices from "@/services/main/subscriptionServices";
import { useAppSelector } from "@lib/hooks";
import { Box, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRightIcon, GraduationCapIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { subscription } from "../../../../../models/subscription";
import CurrencySwitcher from "./CurrencySwitcher";
import PlanCard from "./PlanCard";
import PopupNeed from "./PopupNeed";

interface PlansSectionProps {
  plans: any;
  lang: string;
  currency?: string;
}

export default function PlansSection({
  plans,
  lang,
  currency,
}: PlansSectionProps) {
  const queryClient = useQueryClient();
  const [currentUrl, setcurrentUrl] = useState<string>("");
  const pathName = usePathname();
  const user = queryClient.getQueryData<User>(["user"]);
  const config = queryClient.getQueryData<ConfigData>(["config"]);
  const isLoggedIn = !!user;
  const t = useTranslations("messages");
  const { currencyLocal } = useAppSelector((state) => state.auth);

  const { getCostCurrency } = usePrice();

  useEffect(() => {
    const handlePathChange = () => {
      const path =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      setcurrentUrl(path);
    };
    handlePathChange();
  }, [pathName]);
  // useEffect(() => {
  //   if (typeof window !== "undefined") {
  //     const currencyLocal = localStorage.getItem("currency");
  //     if (currencyLocal) {
  //       setCurrency(currencyLocal);
  //     }
  //   }
  // }, [user]);

  const memoryCurrency = useMemo(() => {
    if (user?.currency) {
      return user.currency;
    }
    if (currency && !currencyLocal) {
      return currency;
    }
    if (currencyLocal) {
      return currencyLocal;
    }
    return "USD";
  }, [currency, currencyLocal, user?.currency]);

  const { data, isLoading } = useQuery({
    queryKey: ["pricing", lang],
    queryFn: async () => {
      const params: paramsSubcription = {
        take: 999,
        sortedField: "amountShow",
        isDescending: false,
        language: lang,
      };
      const response = await SubscriptionServices.getSubscriptions(params);
      if (response?.length === 0) {
        return [];
      }
      return response;
    },
    initialData: plans,
    placeholderData: (prev) => prev,
    refetchOnWindowFocus: false,
  });
  return (
    <div className="flex flex-col gap-[10px]">
      {/* Heading */}
      
      {!user && (
        <Box className="flex justify-center gap-1 items-center">
          <Typography
            className="!font-[Montserrat] !leading-6"
            translation-key="common_pricing_already_subscribe"
          >
            {t("common_pricing_already_subscribe")}
          </Typography>
          <Link
            href={`${APP_ROUTES(lang).LOGIN.to}?redirect=${encodeURIComponent(currentUrl)}`}
            className={
              "!font-[Montserrat] !leading-6 !text-cimigo-blue hover:!text-cimigo-black pricing-plan-link"
            }
            translation-key="common_login_here"
          >
            {t("common_login_here")}
          </Link>
        </Box>
      )}

      <div className="flex flex-col gap-2">
        <h1
          className=" text-center text-3xl md:text-[32px] text-cimigo-blue font-semibold tracking-[-0.8px]"
          translation-key="common_pricing_title"
        >
          {t("common_pricing_title")}
        </h1>
        {/* <p
          className="m-0 text-center text-sm md:text-base text-gray-90 font-normal"
          translation-key="common_pricing_sub_title"
        >
          {t("common_pricing_sub_title")}
        </p> */}
      </div>
      <div
        className={cn("flex", {
          " md:justify-center": data?.length === 1,
        })}
      >
        {data?.length === 0 || !data ? null : (
          <CurrencySwitcher
            currencySSR={memoryCurrency}
            isLoggedIn={isLoggedIn}
          />
        )}
      </div>
      <div
        className={cn("grid grid-cols-1  gap-8 ", {
          "": data?.length === 1,
          "md:grid-cols-2 ": data?.length && data?.length > 1,
          "md:grid-cols-2 lg:grid-cols-3": data?.length && data?.length > 2,
        })}
      >
        {data?.length > 0
          ? data.map((item: subscription) => {
            return (
              <div key={item.id}>
                <PlanCard
                  prevSubscription={item?.preSubscription}
                  trail={item.isTrial}
                  subscriptionId={item.id}
                  title={item.name}
                  price={
                    getCostCurrency(
                      item.amountShow,
                      memoryCurrency,
                      config?.usdToVND,
                      config?.idrToVND,
                    ).show
                  }
                  saving={item.saving}
                  valueNote={item.title}
                  description={item.description}
                  features={item.content}
                  isPopular={item.isPopular}
                  isOnlyOne={data?.length === 1}
                />
              </div>
            );
          })
          : null}
      </div>
      <div className="no-underline flex flex-row gap-2 justify-end items-center">
        <span className="text-ciGray-1000 text-sm/6 tracking-[-0.42px] font-medium">
          <PopupNeed
            type={TYPE_NEED_HELP.SUBSCRIPTION}
            title="common_need_help_?"
          />
        </span>
      </div>
      {!user && (
        <Box className="flex justify-center mb-4">
          <div className="w-full max-w-4xl rounded-2xl border-2 border-dashed border-lime-600 bg-lime-50/70 px-4 py-4 shadow-sm md:px-6">
            <div className="flex flex-col items-center gap-4 text-center md:flex-row md:items-center md:justify-between md:text-left">
              <div className="flex flex-col items-center gap-3 md:flex-row md:items-start md:gap-4">
                <div className="shrink-0 flex">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-lime-200/80 md:h-12 md:w-12">
                    <GraduationCapIcon className="h-5 w-5 text-lime-800 md:h-6 md:w-6" />
                  </div>
                </div>
                <div>
                  <p className="m-0 text-lg font-semibold tracking-[-0.2px] text-lime-900" translation-key="pricing_student_title">
                    {t("pricing_student_title")}
                  </p>
                  <p className="m-0 text-sm leading-6 text-lime-700 font-medium" translation-key="pricing_student_access">
                    {t("pricing_student_access")}
                  </p>
                </div>
              </div>
              <Link
                href={APP_ROUTES(lang).REGISTER.to}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-lime-300 border-solid  bg-white px-5 py-2 text-sm font-semibold text-lime-900 transition hover:bg-lime-200 shrink-0 no-underline sm:w-auto"
                translation-key="pricing_student_register"
              >
                {t("pricing_student_register")}
                <ArrowUpRightIcon className="h-4 w-4 text-lime-900 shrink-0" />
              </Link>
            </div>
          </div>
        </Box>
      )}
    </div>
  );
}
